import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Muestra un texto corto al pasar el mouse por encima; al tocarlo (o en el celular) se queda
// abierto hasta tocar afuera. Se usa, por ejemplo, para el motivo de una cantidad rechazada.
// La burbuja se dibuja sobre toda la pantalla (portal), para que una tabla con desplazamiento
// no la recorte.
function NotaEmergente({ titulo, texto, children }) {
  const [encima, setEncima] = useState(false);
  const [fijo, setFijo] = useState(false);
  const [posicion, setPosicion] = useState(null);
  const disparador = useRef(null);
  const burbuja = useRef(null);
  const visible = encima || fijo;

  const ubicar = () => {
    if (!disparador.current) return;
    const r = disparador.current.getBoundingClientRect();
    const ancho = Math.min(240, window.innerWidth - 16);
    const izquierda = Math.min(Math.max(8, r.left + r.width / 2 - ancho / 2), window.innerWidth - ancho - 8);
    setPosicion({ top: r.bottom + 6, left: izquierda, width: ancho });
  };

  useEffect(() => {
    if (!visible) return undefined;
    ubicar();
    window.addEventListener("scroll", ubicar, true);
    window.addEventListener("resize", ubicar);
    return () => {
      window.removeEventListener("scroll", ubicar, true);
      window.removeEventListener("resize", ubicar);
    };
  }, [visible]);

  useEffect(() => {
    if (!fijo) return undefined;
    const cerrar = (e) => {
      const dentro = disparador.current?.contains(e.target) || burbuja.current?.contains(e.target);
      if (!dentro) setFijo(false);
    };
    const cerrarConEsc = (e) => e.key === "Escape" && setFijo(false);
    document.addEventListener("mousedown", cerrar);
    document.addEventListener("touchstart", cerrar);
    document.addEventListener("keydown", cerrarConEsc);
    return () => {
      document.removeEventListener("mousedown", cerrar);
      document.removeEventListener("touchstart", cerrar);
      document.removeEventListener("keydown", cerrarConEsc);
    };
  }, [fijo]);

  if (!texto) return children;

  return (
    <>
      <button
        ref={disparador}
        type="button"
        onMouseEnter={() => setEncima(true)}
        onMouseLeave={() => setEncima(false)}
        onClick={(e) => {
          e.stopPropagation();
          setFijo((v) => !v);
        }}
        aria-expanded={visible}
        aria-label={`${titulo}: ${texto}`}
        style={estilos.disparador}
      >
        {children}
        <span style={estilos.icono} aria-hidden="true">💬</span>
      </button>
      {visible &&
        posicion &&
        createPortal(
          <span
            ref={burbuja}
            role="tooltip"
            style={{ ...estilos.burbuja, top: posicion.top, left: posicion.left, width: posicion.width }}
            onClick={(e) => e.stopPropagation()}
          >
            <span style={estilos.titulo}>{titulo}</span>
            <span style={estilos.texto}>“{texto}”</span>
          </span>,
          document.body
        )}
    </>
  );
}

const estilos = {
  disparador: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    color: "inherit",
    cursor: "pointer",
    textDecoration: "underline dotted",
    textUnderlineOffset: 3,
  },
  icono: { fontSize: "0.8rem", textDecoration: "none" },
  burbuja: {
    position: "fixed",
    zIndex: 3000,
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
    padding: "0.55rem 0.7rem",
    background: "#fff",
    border: "1px solid #f1c2b9",
    borderLeft: "4px solid #a3402f",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(27,42,74,0.18)",
    textAlign: "left",
    whiteSpace: "normal",
    fontWeight: 400,
    boxSizing: "border-box",
  },
  titulo: { fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.03em", textTransform: "uppercase", color: "#a3402f" },
  texto: { fontSize: "0.82rem", color: "#3d4a5c", fontStyle: "italic", wordBreak: "break-word" },
};

export default NotaEmergente;
